import { Empty, Form, Input, message, Modal, Radio } from 'antd'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { updateCurrentCustomerApi } from '@/network/api/customerContext'
import { request } from '@/network/axios'
import { getAccessToken } from '@/network/tokenManager'
import type { CustomerOption } from '@/store/customerContext'
import { CUSTOMER_SELECTION_REQUIRED_EVENT, useCustomerContextStore } from '@/store/customerContext'
import { useUserStore } from '@/store/user'

import { resolveCustomerPickerBehavior } from './customer-picker'

export interface CustomerSwitcherRenderState {
  currentCustomer: CustomerOption | null
  customerCount: number
  loading: boolean
  openCustomerPicker: () => void
  openCustomerEditor: () => void
}

interface CustomerSwitcherProps {
  children: (state: CustomerSwitcherRenderState) => ReactNode
}

export default function CustomerSwitcher({ children }: CustomerSwitcherProps) {
  const userId = useUserStore((state) => state.userInfo.id)
  const resetUser = useUserStore((state) => state.resetUser)
  const { customers, currentCustomerId, selectionRequired, selectCustomer, updateCustomer } = useCustomerContextStore()
  const [loading, setLoading] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [candidateId, setCandidateId] = useState<number | null>(null)
  const [form] = Form.useForm<{ name: string; companyName?: string }>()
  const navigate = useNavigate()

  const current = useMemo(
    () => customers.find((customer) => customer.id === currentCustomerId) ?? null,
    [customers, currentCustomerId],
  )
  const pickerBehavior = resolveCustomerPickerBehavior(customers.length, selectionRequired, candidateId)

  const loadCustomers = useCallback(async () => {
    const token = getAccessToken()
    if (!userId || !token) return
    setLoading(true)
    try {
      await request.loadCustomerContext(userId)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void loadCustomers()
  }, [loadCustomers])

  const openCustomerPicker = () => {
    setCandidateId(currentCustomerId)
    setPickerOpen(true)
    void loadCustomers()
  }

  useEffect(() => {
    if (selectionRequired) {
      setCandidateId(null)
      setPickerOpen(true)
    }
  }, [selectionRequired])

  useEffect(() => {
    const openPicker = () => {
      setPickerOpen(true)
      void loadCustomers()
    }
    window.addEventListener(CUSTOMER_SELECTION_REQUIRED_EVENT, openPicker)
    return () => window.removeEventListener(CUSTOMER_SELECTION_REQUIRED_EVENT, openPicker)
  }, [loadCustomers])

  const switchCustomer = (customerId: number) => {
    const changed = currentCustomerId != null && customerId !== currentCustomerId
    selectCustomer(customerId)
    setPickerOpen(false)
    if (changed) window.location.reload()
  }

  const exitWithoutCustomer = () => {
    setPickerOpen(false)
    resetUser()
    navigate('/login', { replace: true })
  }

  const submitCustomerSelection = () => {
    if (pickerBehavior.hasNoCustomers) {
      exitWithoutCustomer()
      return
    }
    if (candidateId != null) switchCustomer(candidateId)
  }

  const cancelCustomerSelection = () => {
    if (pickerBehavior.cancelExits) {
      exitWithoutCustomer()
      return
    }
    setPickerOpen(false)
  }

  const openCustomerEditor = () => {
    if (!current) return
    form.setFieldsValue({ name: current.name, companyName: current.companyName ?? undefined })
    setEditOpen(true)
  }

  const submitEdit = async () => {
    const values = await form.validateFields().catch(() => null)
    if (!values) return
    try {
      const response = await updateCurrentCustomerApi(values)
      updateCustomer(response.data)
      setEditOpen(false)
      message.success('客户资料已更新')
    } catch {
      message.error('客户资料更新失败，请检查后重试')
    }
  }

  return (
    <>
      {children({
        currentCustomer: current,
        customerCount: customers.length,
        loading,
        openCustomerPicker,
        openCustomerEditor,
      })}

      <Modal
        title="选择当前客户"
        open={pickerOpen}
        closable={!pickerBehavior.mustResolveCustomer}
        maskClosable={false}
        keyboard={!pickerBehavior.mustResolveCustomer}
        cancelText={pickerBehavior.cancelText}
        cancelButtonProps={{ style: pickerBehavior.hideCancel ? { display: 'none' } : undefined }}
        okText={pickerBehavior.okText}
        okButtonProps={{ disabled: pickerBehavior.okDisabled }}
        onCancel={cancelCustomerSelection}
        onOk={submitCustomerSelection}
      >
        {pickerBehavior.hasNoCustomers ? (
          <Empty description="当前账号没有可用客户，请联系管理员" />
        ) : (
          <Radio.Group className="w-full" value={candidateId} onChange={(event) => setCandidateId(event.target.value)}>
            <div className="flex flex-col gap-[10px]">
              {customers.map((customer) => (
                <Radio key={customer.id} value={customer.id}>
                  {customer.name}
                  {customer.companyName ? ` · ${customer.companyName}` : ''}
                </Radio>
              ))}
            </div>
          </Radio.Group>
        )}
      </Modal>

      <Modal
        title="修改客户资料"
        open={editOpen}
        okText="保存"
        onCancel={() => setEditOpen(false)}
        onOk={() => void submitEdit()}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="客户名称" rules={[{ required: true, message: '请输入客户名称' }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="companyName" label="企业名称">
            <Input maxLength={200} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
